import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import PropTypes from "prop-types";
import { useMemo } from "react";

import { tableCellListBadgeContent } from "@components/table-cells/cell.utils";
import {
  DocumentsIcon,
  HyphenIcon,
  Link,
  OverflowText,
  TextTooltip,
} from "@transfr-inc/dashboard-components";
import { Product } from "@transfr-inc/dashboard-components/constants";
import {
  Column,
  ColumnOptions,
  ColumnType,
  DataTable,
  SortDirection,
} from "@transfr-inc/dashboard-components/data-table";
import { getProduct } from "@transfr-inc/dashboard-components/utils";

import { ClickDocumentEvent } from "@views/curriculum-resources/tracking";
import { attributeKey } from "@views/curriculum-resources/utils/constants";

import "./documents.table.scss";

const productColumns = {
  [Product.TS]: [
    "orderNum",
    "file",
    "name",
    "activity",
    "industry",
    "simulation",
    "durationMinutes",
  ],
  [Product.PROG]: [
    "orderNum",
    "file",
    "name",
    "activity",
    "industry",
    "simulation",
    "durationMinutes",
  ],
  [Product.TRK]: ["orderNum", "file", "name", "activity"],
};

const columns = [
  new Column(
    "orderNum",
    "Order",
    ColumnType.BASIC,
    new ColumnOptions({
      sortingEnabled: true,
      sortKeyFunctions: [(row) => row.orderNum],
    })
  ),
  new Column(
    "file",
    "File",
    ColumnType.CUSTOM,
    new ColumnOptions({
      sortingEnabled: true,
      sortKeyFields: ["mimeType"],
      overflowEnabled: false,
      customFunction: (_, row) => <DocumentsIcon mimeType={row.mimeType} />,
    })
  ),
  new Column(
    "name",
    "Name",
    ColumnType.CUSTOM,
    new ColumnOptions({
      sortingEnabled: true,
      overflowEnabled: true,
      customFunction: (value, row) => {
        return (
          <div className="documents-table-name-container">
            <OverflowText text={value}>
              <Link href={row.url} onClick={row.trackingFn}>
                {value}
              </Link>
            </OverflowText>
            {row.description && (
              <TextTooltip text={row.description}>
                <FontAwesomeIcon icon={"fa-regular fa-circle-info"} />
              </TextTooltip>
            )}
          </div>
        );
      },
    })
  ),
  new Column(
    "activity",
    "Activity Type",
    ColumnType.CUSTOM,
    new ColumnOptions({
      sortingEnabled: true,
      sortKeyFunctions: [(row) => row.activity.name],
      overflowEnabled: true,
      customFunction: (value) => (
        <OverflowText text={value?.name}>{value?.name}</OverflowText>
      ),
    })
  ),
  new Column(
    "industry",
    "Industry",
    ColumnType.CUSTOM,
    new ColumnOptions({
      sortingEnabled: true,
      sortKeyFunctions: [
        (row) =>
          row.attributes
            .find((attribute) => attribute.key === attributeKey.industry)
            ?.values?.join(","),
      ],
      overflowEnabled: true,
      customFunction: (_, row) => {
        const industries = row.attributes.find(
          (attribute) => attribute.key === attributeKey.industry
        )?.values;
        return tableCellListBadgeContent(industries);
      },
    })
  ),
  new Column(
    "simulation",
    "Simulation",
    ColumnType.CUSTOM,
    new ColumnOptions({
      sortingEnabled: true,
      sortKeyFunctions: [
        (row) =>
          row.attributes
            .find((attribute) => attribute.key === attributeKey.simulationName)
            ?.values?.join(","),
      ],
      overflowEnabled: true,
      customFunction: (_, row) => {
        const simulations = row.attributes.find(
          (attribute) => attribute.key === attributeKey.simulationName
        )?.values;
        return tableCellListBadgeContent(simulations);
      },
    })
  ),
  new Column(
    "durationMinutes",
    "Est Time",
    ColumnType.CUSTOM,
    new ColumnOptions({
      sortingEnabled: true,
      overflowEnabled: true,
      customFunction: (value) => (value ? `${value}min` : <HyphenIcon />),
    })
  ),
];

const keyFields = ["id"];

export const DocumentsTable = ({
  data,
  className,
  responsiveStyles,
  product,
  tracking,
  trackingData,
}) => {
  const displayColumns = useMemo(
    () =>
      columns.filter((column) =>
        productColumns[product.productId].includes(column.id)
      ),
    [product]
  );

  const dataWithOrderNum = useMemo(() => {
    return data.map((item, index) => ({
      ...item,
      orderNum: index + 1,
      trackingFn: () =>
        tracking.trackEvent(new ClickDocumentEvent(item, trackingData)),
    }));
  }, [data, trackingData]);

  return (
    <DataTable
      className={clsx(
        "documents-table",
        getProduct(product.productId)?.name?.toLowerCase(),
        className
      )}
      defaultSortPropName="orderNum"
      defaultSortDirection={SortDirection.ASCENDING}
      columns={displayColumns}
      data={dataWithOrderNum}
      keyFields={keyFields}
      responsiveStyles={responsiveStyles}
    />
  );
};

DocumentsTable.propTypes = {
  data: PropTypes.array.isRequired,
  className: PropTypes.string,
  responsiveStyles: PropTypes.object,
  product: PropTypes.object,
  trackingData: PropTypes.shape({
    curriculumLevelId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    curriculumLevelParentId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
  }),
};
